/**
 * ItemPersonalizationWidget — Experience Widget for the Item detail page.
 *
 * Shows an empty state when no template is linked, or a compact summary
 * with quick actions when a template is linked. Supports:
 * - Create Template (Sheet) + auto-link
 * - Link Existing Template (Combobox)
 * - Field summary, rule summary
 * - Quick-add field (reuses FieldSheet)
 * - Edit Template navigation
 * - Unlink Template with confirmation
 *
 * Plan: 117-personalization-admin-ui, Epic 03
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Paintbrush,
  Plus,
  Link2,
  ExternalLink,
  Unlink,
  Check,
  ChevronsUpDown,
} from "lucide-react";

import { useUpdateDoc } from "@/lib/hooks/useFrappeQuery";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useAuthStore } from "@/lib/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { TemplateStatusBadge } from "./components/TemplateStatusBadge";
import { TemplateTranslateButton } from "./components/TemplateTranslateButton";
import { FieldSheet } from "./components/FieldSheet";
import { getFieldTypeIcon } from "./components/FieldTypeRadioCards";
import {
  useTemplateDetail,
  useTemplateList,
  useAddField,
  useBundleConfig,
  customizationQueryKeys,
} from "./api/customizationAdminHooks";
import { BundlePersonalizationView } from "./components/BundlePersonalizationView";
import { customizationAdminApi } from "./api/customizationAdminApi";
import type { TemplateField } from "./api/customizationAdminTypes";

// ─── Component ──────────────────────────────────────────────

export function ItemPersonalizationWidget({
  formData,
  onFieldChange,
}: ExperienceWidgetProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.currentTenant);

  const itemName = formData?.name as string | undefined;
  const templateName = formData?.customization_template as string | undefined;

  // Hooks — called unconditionally at top level (React rules)
  const updateItem = useUpdateDoc("Item", itemName || "");
  const addFieldMutation = useAddField();
  const templateDetail = useTemplateDetail(templateName || "");
  const bundleConfig = useBundleConfig(itemName || "");

  // UI state
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [fieldSheetOpen, setFieldSheetOpen] = useState(false);

  // Hide for bookable items — personalization not relevant for bookings
  if (formData?.is_bookable === 1 || formData?.is_bookable === true) return null;

  // Bundle loading — show skeleton while checking if this is a bundle
  if (bundleConfig.isLoading) {
    return (
      <div>
        <SectionHeader label="Personalization" />
        <div className="rounded-2xl bg-card shadow-apple p-6 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  // Bundle detected — bundle view takes priority over single-item view
  if (bundleConfig.data?.is_bundle) {
    return (
      <div>
        <SectionHeader label="Personalization" />
        <BundlePersonalizationView
          config={bundleConfig.data}
          itemCode={itemName || ""}
        />
      </div>
    );
  }

  if (templateName && templateDetail.data) {
    return (
      <div>
        <SectionHeader label="Personalization" />
        <LinkedState
          template={templateDetail.data}
          templateName={templateName}
          itemName={itemName}
          updateItem={updateItem}
          addFieldMutation={addFieldMutation}
          onFieldChange={onFieldChange}
          fieldSheetOpen={fieldSheetOpen}
          setFieldSheetOpen={setFieldSheetOpen}
          navigate={navigate}
        />
      </div>
    );
  }

  if (templateName && templateDetail.isLoading) {
    return (
      <div>
        <SectionHeader label="Personalization" />
        <div className="rounded-2xl bg-card shadow-apple p-6 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader label="Personalization" />
      <EmptyState
        itemName={itemName}
        updateItem={updateItem}
        onFieldChange={onFieldChange}
        createSheetOpen={createSheetOpen}
        setCreateSheetOpen={setCreateSheetOpen}
        linkPopoverOpen={linkPopoverOpen}
        setLinkPopoverOpen={setLinkPopoverOpen}
        tenant={tenant}
        queryClient={queryClient}
      />
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────

function EmptyState({
  itemName,
  updateItem,
  onFieldChange,
  createSheetOpen,
  setCreateSheetOpen,
  linkPopoverOpen,
  setLinkPopoverOpen,
  tenant,
  queryClient,
}: {
  itemName: string | undefined;
  updateItem: ReturnType<typeof useUpdateDoc>;
  onFieldChange: (fieldname: string, value: any) => void;
  createSheetOpen: boolean;
  setCreateSheetOpen: (open: boolean) => void;
  linkPopoverOpen: boolean;
  setLinkPopoverOpen: (open: boolean) => void;
  tenant: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  return (
    <>
      <div className="rounded-2xl bg-card shadow-apple p-8 flex flex-col items-center text-center gap-4">
        <div className="flex items-center justify-center h-14 w-14 rounded-md bg-muted">
          <Paintbrush className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground max-w-xs">
          Let customers personalize this product with custom text, file uploads,
          and options.
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreateSheetOpen(true)}
            disabled={!itemName}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create Template
          </Button>
          <LinkExistingButton
            open={linkPopoverOpen}
            onOpenChange={setLinkPopoverOpen}
            itemName={itemName}
            updateItem={updateItem}
            onFieldChange={onFieldChange}
          />
        </div>
      </div>

      <CreateTemplateSheet
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
        itemName={itemName}
        updateItem={updateItem}
        onFieldChange={onFieldChange}
        tenant={tenant}
        queryClient={queryClient}
      />
    </>
  );
}

// ─── Create Template Sheet ──────────────────────────────────

function CreateTemplateSheet({
  open,
  onOpenChange,
  itemName,
  updateItem,
  onFieldChange,
  tenant,
  queryClient,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string | undefined;
  updateItem: ReturnType<typeof useUpdateDoc>;
  onFieldChange: (fieldname: string, value: any) => void;
  tenant: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"Draft" | "Active">("Draft");
  const [saving, setSaving] = useState(false);

  const handleCreate = useCallback(async () => {
    if (!name.trim() || !itemName) return;
    setSaving(true);
    try {
      const result = await customizationAdminApi.save({
        template_name: name.trim(),
        description: "",
        status,
        fields: [],
        rules: [],
      });
      try {
        await updateItem.mutateAsync({
          customization_template: result.name,
        });
        onFieldChange("customization_template", result.name);
        toast.success("Template created and linked");
      } catch {
        toast.error(
          `Template "${name.trim()}" created but couldn't be linked. Link it manually.`
        );
      }
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
      onOpenChange(false);
      setName("");
      setStatus("Draft");
    } catch {
      toast.error("Failed to create template");
    } finally {
      setSaving(false);
    }
  }, [name, status, itemName, updateItem, onFieldChange, queryClient, tenant, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Create Template</SheetTitle>
          <SheetDescription>
            Create a new customization template and link it to this product.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 py-4">
          <div className="space-y-2">
            <Label htmlFor="create-template-name">Template Name</Label>
            <Input
              id="create-template-name"
              placeholder="e.g., Embroidery Options"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="create-template-status">Active</Label>
            <Switch
              id="create-template-status"
              checked={status === "Active"}
              onCheckedChange={(checked) =>
                setStatus(checked ? "Active" : "Draft")
              }
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || saving}
          >
            {saving ? "Creating…" : "Create & Link"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Link Existing Template Combobox ────────────────────────

function LinkExistingButton({
  open,
  onOpenChange,
  itemName,
  updateItem,
  onFieldChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemName: string | undefined;
  updateItem: ReturnType<typeof useUpdateDoc>;
  onFieldChange: (fieldname: string, value: any) => void;
}) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const { data } = useTemplateList(0, 100, debouncedSearch, "");

  const handleSelect = useCallback(
    async (selectedName: string) => {
      if (!itemName) return;
      try {
        await updateItem.mutateAsync({
          customization_template: selectedName,
        });
        onFieldChange("customization_template", selectedName);
        toast.success("Template linked");
        onOpenChange(false);
      } catch {
        toast.error("Failed to link template");
      }
    },
    [itemName, updateItem, onFieldChange, onOpenChange]
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={!itemName}>
          <Link2 className="h-4 w-4 mr-1.5" />
          Link Existing
          <ChevronsUpDown className="h-3.5 w-3.5 ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search templates…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              No templates found.
            </CommandEmpty>
            {data?.templates.map((t) => (
              <CommandItem
                key={t.name}
                value={t.name}
                onSelect={() => handleSelect(t.name)}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Check className="h-4 w-4 opacity-0" />
                  <span className="truncate">{t.template_name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-muted-foreground">
                    {t.field_count}f
                  </span>
                  <TemplateStatusBadge status={t.status} />
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Linked State ───────────────────────────────────────────

function LinkedState({
  template,
  templateName,
  itemName,
  updateItem,
  addFieldMutation,
  onFieldChange,
  fieldSheetOpen,
  setFieldSheetOpen,
  navigate,
}: {
  template: import("./api/customizationAdminTypes").TemplateDetail;
  templateName: string;
  itemName: string | undefined;
  updateItem: ReturnType<typeof useUpdateDoc>;
  addFieldMutation: ReturnType<typeof useAddField>;
  onFieldChange: (fieldname: string, value: any) => void;
  fieldSheetOpen: boolean;
  setFieldSheetOpen: (open: boolean) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const handleUnlink = useCallback(async () => {
    if (!itemName) return;
    try {
      await updateItem.mutateAsync({ customization_template: "" });
      onFieldChange("customization_template", "");
      toast.success("Template unlinked");
    } catch {
      toast.error("Failed to unlink template");
    } finally {
      setUnlinkOpen(false);
    }
  }, [itemName, updateItem, onFieldChange]);

  const handleAddField = useCallback(
    (field: Omit<TemplateField, "name" | "sort_order">) => {
      addFieldMutation.mutate({
        template_name: templateName,
        label: field.label,
        field_type: field.field_type,
        required: field.required,
        options: field.options,
        addon_item: field.addon_item,
        max_characters: field.max_characters,
        allowed_file_types: field.allowed_file_types,
        max_file_size: field.max_file_size,
      });
    },
    [templateName, addFieldMutation]
  );

  return (
    <>
      <div className="rounded-2xl bg-card shadow-apple overflow-hidden">
        {/* Header: template name + status + translate */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <span className="text-sm font-semibold truncate">
            {template.template_name}
          </span>
          <div className="flex items-center gap-1.5 shrink-0">
            <TemplateTranslateButton
              templateDocname={templateName}
              templateDisplayName={template.template_name}
              fields={template.fields}
            />
            <TemplateStatusBadge status={template.status} />
          </div>
        </div>

        <div className="mx-5 border-t border-border/40" />

        {/* Fields */}
        <div className="px-5 py-3">
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Fields ({template.fields.length})
          </div>
          {template.fields.length > 0 ? (
            <div className="space-y-1.5">
              {template.fields.map((f, i) => {
                const Icon = getFieldTypeIcon(f.field_type);
                return (
                  <div
                    key={f.name || i}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{f.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {f.field_type}
                    </span>
                    {f.required === 1 && (
                      <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-md">
                        Required
                      </span>
                    )}
                    {f.addon_price > 0 && (
                      <span className="text-[10px] font-medium text-sky-600 ml-auto">
                        +฿{f.addon_price}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No fields yet</p>
          )}
        </div>

        {/* Rules */}
        {template.rules.length > 0 && (
          <>
            <div className="mx-5 border-t border-border/40" />
            <div className="px-5 py-3">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Rules ({template.rules.length})
              </div>
              <div className="space-y-1">
                {template.rules.map((r, i) => (
                  <p
                    key={r.name || i}
                    className="text-sm text-muted-foreground italic"
                  >
                    {r.summary || "—"}
                  </p>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mx-5 border-t border-border/40" />

        {/* Actions */}
        <div className="px-5 py-4 flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFieldSheetOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Field
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(
                `/inventory/customization-templates/${encodeURIComponent(templateName)}`
              )
            }
          >
            Edit Template
            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </Button>

          <div className="ml-auto">
            <AlertDialog open={unlinkOpen} onOpenChange={setUnlinkOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground">
                  <Unlink className="h-4 w-4 mr-1.5" />
                  Unlink
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
                <AlertDialogHeader>
                  <AlertDialogTitle>Unlink template?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove personalization from this product.
                    The template itself won't be deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleUnlink}>
                    Unlink
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Quick-add Field Sheet (reuse from Epic 01) */}
      <FieldSheet
        open={fieldSheetOpen}
        onOpenChange={setFieldSheetOpen}
        onSave={handleAddField}
      />
    </>
  );
}
